import React from 'react';
import { clsx } from 'clsx';
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Clock,
  Check,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';

export type PickBadgeVariant =
  | 'boom'
  | 'bust'
  | 'fair'
  | 'steal'
  | 'reach'
  | 'pending'
  | 'done'
  | 'warning';

interface PickBadgeProps {
  variant: PickBadgeVariant;
  /** Optional text next to the glyph, e.g. "Steal" or "+31.2". */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const VARIANTS: Record<
  PickBadgeVariant,
  { Icon: React.ComponentType<{ className?: string }>; classes: string; title: string }
> = {
  boom: {
    Icon: ArrowUpRight,
    classes: 'bg-accent-primary/15 text-accent-primary border-accent-primary/30',
    title: 'Boom pick',
  },
  bust: {
    Icon: ArrowDownRight,
    classes: 'bg-accent-danger/15 text-accent-danger border-accent-danger/30',
    title: 'Bust pick',
  },
  fair: {
    Icon: Minus,
    classes: 'bg-white/5 text-text-secondary border-fieldline',
    title: 'Fair value',
  },
  steal: {
    Icon: TrendingDown,
    classes: 'bg-accent-primary/15 text-accent-primary border-accent-primary/30',
    title: 'ADP steal',
  },
  reach: {
    Icon: TrendingUp,
    classes: 'bg-accent-danger/15 text-accent-danger border-accent-danger/30',
    title: 'ADP reach',
  },
  pending: {
    Icon: Clock,
    classes: 'bg-white/5 text-text-tertiary border-fieldline',
    title: 'Pending',
  },
  done: {
    Icon: Check,
    classes: 'bg-accent-primary/15 text-accent-primary border-accent-primary/30',
    title: 'Complete',
  },
  warning: {
    Icon: AlertTriangle,
    classes: 'bg-accent-warning/15 text-accent-warning border-accent-warning/30',
    title: 'Warning',
  },
};

/**
 * Drawn status badge — replaces all emoji indicators (🔥/❌/⏳/✓) in the UI.
 */
const PickBadge: React.FC<PickBadgeProps> = ({ variant, label, size = 'sm', className }) => {
  const { Icon, classes, title } = VARIANTS[variant];
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1 rounded-md border font-mono font-semibold uppercase tracking-wide',
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs',
        classes,
        className
      )}
    >
      <Icon className={iconSize} aria-hidden />
      {label && <span>{label}</span>}
    </span>
  );
};

export default PickBadge;
